import React, { useRef, useState } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore from 'swiper'
import { ResponsiveImage } from '../Shared/ResponsiveImage'

import 'swiper/scss'
import 'swiper/scss/pagination'
import 'swiper/css/free-mode'
import 'swiper/scss/navigation'
import 'swiper/css/thumbs'
import {
  FreeMode,
  Navigation,
  Pagination,
  Thumbs,
  Mousewheel,
} from 'swiper/modules'
import type {
  CMSComponentResponsiveImage,
  CMSComponentVideo,
  CMSImage,
} from '@/types/sanity'
import { Video } from '../Shared/Video'

interface CarouselProps {
  imageryCarousel: (
    | CMSImage
    | CMSComponentVideo
    | CMSComponentResponsiveImage
  )[]
}

import './Carousel.scss'
import { DialogSlider } from '../DialogSlider/DialogSlider'

export const Carousel: React.FC<CarouselProps> = ({ imageryCarousel }) => {
  const [selectedCarouselItem, setSelectedCarouselItem] = useState(0)
  const [showDialogSlider, setShowDialogSlider] = useState(false)
  const swiperRef = useRef<SwiperCore | null>(null)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null)

  const handleImageClick = (index: number) => {
    setSelectedCarouselItem(index)
    setShowDialogSlider(true)
  }

  const handleDialogClose = (newIndex: number) => {
    setShowDialogSlider(false)
    if (newIndex !== undefined) {
      setSelectedCarouselItem(newIndex)
      swiperRef.current?.slideTo(newIndex)
    }
  }

  return (
    <div className="buying-panel-carousel">
      <div className="desktop-slider">
        <div className="nutrition-hero__carousel">
          <div className="nutrition-hero__carousel-list">
            <Swiper
              spaceBetween={10}
              slidesPerView={'auto'}
              direction={'vertical'}
              freeMode={true}
              mousewheel={true}
              modules={[FreeMode, Thumbs, Mousewheel]}
              onSwiper={setThumbsSwiper}
            >
              {imageryCarousel.map((carouselItem, index) => (
                <SwiperSlide
                  key={index}
                  className={`carousel-item ${
                    index === selectedCarouselItem && 'active-carousel-item'
                  }`}
                >
                  {carouselItem._type === 'component.responsiveImage' && (
                    <ResponsiveImage
                      document={carouselItem}
                      overrides={{
                        mobileAspectRatio: '1/1',
                        customSizes: [640, 768, 1024, 1200],
                      }}
                    />
                  )}
                  {carouselItem._type === 'component.video' && (
                    <ResponsiveImage
                      document={carouselItem.backupImage}
                      overrides={{
                        mobileAspectRatio: '1/1',
                        customSizes: [640, 768, 1024, 1200],
                      }}
                    />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <div className="selected-carousel-slide">
            <Swiper
              onSlideChange={(slide) => {
                setSelectedCarouselItem(slide.realIndex)
                swiperRef.current = slide
              }}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[Pagination, Navigation, FreeMode, Thumbs]}
              slidesPerView={1}
              pagination={{ clickable: true }}
              scrollbar={{ draggable: true }}
              navigation={true}
              loop={true}
              initialSlide={selectedCarouselItem}
              onClick={() => {
                setShowDialogSlider(true)
              }}
            >
              {imageryCarousel.map((carouselItem, index) => {
                return (
                  <SwiperSlide key={index} className="carousel-swiper-slide">
                    <div
                      className={`${
                        index === selectedCarouselItem && 'active-carousel-item'
                      }`}
                    >
                      {carouselItem._type === 'component.responsiveImage' && (
                        <ResponsiveImage
                          document={carouselItem}
                          overrides={{
                            objectFit: 'cover',
                            mobileAspectRatio: '1/1',
                            customSizes: [640, 768, 1024, 1200],
                          }}
                        />
                      )}
                      {carouselItem._type === 'component.video' && (
                        <Video
                          videoUrl={carouselItem.video.secureUrl}
                          videoType={carouselItem.videoType}
                          classes="carousel__background-video carousel__background-video--desktop carousel__background-video--mobile"
                          loopVideo={carouselItem.background?.loopVideo}
                          isFirstSection={false}
                          backupImage={carouselItem.backupImage}
                        />
                      )}
                    </div>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </div>
        </div>
      </div>
      <div className="swiper-container mobile-swiper">
        <Swiper
          spaceBetween={12}
          slidesPerView="auto"
          initialSlide={selectedCarouselItem}
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
          loop={true}
          touchEventsTarget="container"
        >
          {imageryCarousel.map((carouselItem, index) => {
            return (
              <SwiperSlide
                key={index}
                className="swiper-slide"
                onClick={() => handleImageClick(index)}
              >
                {carouselItem._type === 'component.responsiveImage' && (
                  <ResponsiveImage
                    document={carouselItem}
                    overrides={{
                      mobileAspectRatio: '1/1',
                      customSizes: [640, 768, 1024, 1200],
                    }}
                  />
                )}
                {carouselItem._type === 'component.video' && (
                  <Video
                    videoUrl={carouselItem.video.secureUrl}
                    videoType={carouselItem.videoType}
                    classes="carousel__background-video carousel__background-video--desktop carousel__background-video--mobile"
                    loopVideo={carouselItem.background?.loopVideo}
                    isFirstSection={false}
                    backupImage={carouselItem.backupImage}
                  />
                )}
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
      <DialogSlider
        isOpen={showDialogSlider}
        onClose={handleDialogClose}
        imageryCarousel={imageryCarousel}
        selectedItem={selectedCarouselItem}
      />
    </div>
  )
}
